import { useCallback } from "react";
import { router, useLocalSearchParams } from "expo-router";
import {
  clampGranularityToWindow,
  defaultGranularityForDays,
  effectiveWindowDays,
  TRAFFIC_GRANULARITIES,
  type TrafficClass,
  type TrafficFilters,
  type TrafficGranularity,
} from "../filters/traffic-filters";

/**
 * Bridges the RoadSense trend filters to the URL query string. Filters live in
 * the URL (per app practices: "Filters are URL params") so a filtered report is
 * shareable — paste the link and a colleague sees the same granularity, window,
 * movements, and classes.
 *
 * `spanDays` is the trend's visible span in days; it decides the **default**
 * granularity (hour … month — see `defaultGranularityForDays`), so a
 * report opens on a readable bucket and an absent `granularity` param resolves
 * to that default. It's `undefined` until the payload loads, where we fall back
 * to the finest default.
 *
 * `dataBounds` (the trend's first/last calendar day) lets us clamp a granularity
 * that the *selected* window can't support — so a shared link carrying, say,
 * `granularity=hour` over a year-long window resolves to Day rather than trying
 * to plot thousands of points. The interactive case (widening the date picker)
 * is clamped in `TrafficFilters`; this covers the on-load / pasted-URL case.
 * Omitted until the payload loads (no bounds → no clamp).
 *
 * Only non-default values are written, so a pristine report keeps a clean URL
 * (no filter noise). Unknown/garbage param values fall
 * back to the default rather than throwing, so a hand-edited URL can't break
 * the screen. Unknown movement ids survive parsing here (we can't enum-check
 * payload-defined ids) but are resolved away downstream by `selectMovements`.
 *
 * Param keys are readable and stable: `granularity`, `classification` (a
 * comma-list of classes), `movement` (a comma-list of movement ids), `from`,
 * `to`.
 */
export function useTrafficFiltersParams(
  spanDays?: number,
  dataBounds?: { min: string; max: string },
): {
  filters: TrafficFilters;
  setFilters: (next: TrafficFilters) => void;
} {
  const params = useLocalSearchParams<{
    granularity?: string;
    classification?: string;
    movement?: string;
    from?: string;
    to?: string;
  }>();

  const defaultGranularity = defaultGranularityForDays(spanDays ?? 0);

  const parsed: TrafficFilters = {
    granularity: parseGranularity(params.granularity, defaultGranularity),
    classifications: parseClasses(params.classification),
    movements: parseList(params.movement),
    fromDate: parseIsoDate(params.from),
    toDate: parseIsoDate(params.to),
  };

  // Clamp a window-incompatible granularity (e.g. from a pasted URL) down to Day
  // once we know the data bounds; before then, render as-parsed.
  //
  // By design we do NOT write the clamped value back to the URL: the param is
  // the user's *stated* intent, not the resolved view. Rewriting it on load
  // would (a) mutate someone's shared link out from under them, and (b) be
  // wrong if they later narrow the date range — the original `hour` should come
  // back, which it can't if we've already overwritten the param with `day`.
  // Bugbot flags the URL/state divergence as a desync; that divergence is the
  // intended behaviour. The cost is a cosmetic one (a freshly opened shared link
  // can show "Reset filters"), which we accept over clobbering intent.
  const filters =
    dataBounds === undefined ? parsed : (
      clampGranularityToWindow(parsed, effectiveWindowDays(parsed, dataBounds.min, dataBounds.max))
    );

  const setFilters = useCallback(
    (next: TrafficFilters) => {
      // `setParams` merges into the existing query, leaving the path (and so the
      // report's `reportId` segment) untouched. Clearing a value back to its
      // (data-aware) default removes the param so a pristine report keeps a
      // clean URL.
      router.setParams({
        granularity: next.granularity === defaultGranularity ? undefined : next.granularity,
        classification: next.classifications.length === 0 ? undefined : next.classifications.join(","),
        movement: next.movements.length === 0 ? undefined : next.movements.join(","),
        from: next.fromDate ?? undefined,
        to: next.toDate ?? undefined,
      });
    },
    [defaultGranularity],
  );

  return { filters, setFilters };
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse the granularity param, falling back to the data-aware default. */
function parseGranularity(value: string | undefined, fallback: TrafficGranularity): TrafficGranularity {
  return TRAFFIC_GRANULARITIES.includes(value as TrafficGranularity) ? (value as TrafficGranularity) : fallback;
}

/** Split a `a,b,c` param into a deduped, order-preserving list of non-empty values. */
function parseList(value: string | undefined): string[] {
  if (typeof value !== "string" || value === "") return [];
  const seen = new Set<string>();
  for (const part of value.split(",")) {
    const trimmed = part.trim();
    if (trimmed !== "") seen.add(trimmed);
  }
  return [...seen];
}

/** Parse the class list, dropping anything that isn't a known class. */
function parseClasses(value: string | undefined): TrafficClass[] {
  return parseList(value).filter((v): v is TrafficClass => v === "car" || v === "truck" || v === "bus");
}

function parseIsoDate(value: string | undefined): string | null {
  return typeof value === "string" && ISO_DATE_RE.test(value) ? value : null;
}
